/* eslint-disable no-param-reassign */
import React from 'react';
import produce from 'immer';

import { location as UrlHelperLocation } from 'utils/url-helper';
import { useClientSelector } from 'client/reducers';
import Analytics, { GoogleAnalyticsObserver, StorycardsAnalyticsObserver } from './Analytics';
import type { IMedia as IAnalyticsMedia } from './Analytics';

interface IMediaContext {
	analytics: Analytics;
	activeMediaId: string | null;
	medias: IAnalyticsMedia[];
}

interface IMediaDispatchContext {
	dispatch: React.Dispatch<Action>;
}

type Action =
	| { type: 'SET_ACTIVE_MEDIA_ID'; payload: { id: string; autoplay?: boolean } }
	| {
			type: 'REGISTER_MEDIA';
			payload: Omit<IAnalyticsMedia, 'isPaused' | 'storyId' | 'cardId' | 'playbackId'> & {
				isPaused?: IAnalyticsMedia['isPaused'];
			};
	  }
	| { type: 'UNREGISTER_MEDIA'; payload: string };

function mediaReducer(state: IMediaContext, action: Action): IMediaContext {
	return produce(state, draft => {
		switch (action.type) {
			case 'REGISTER_MEDIA': {
				if (!draft.medias.find(media => media.id === action.payload.id)) {
					// @ts-expect-error video.js v8.19.1 has incomplete types
					draft.medias.push({ ...action.payload, isPaused: Boolean(action.payload.isPaused) });
				}
				break;
			}
			case 'UNREGISTER_MEDIA': {
				draft.medias = draft.medias.filter(({ id }) => id !== action.payload);
				if (draft.activeMediaId === action.payload) {
					draft.activeMediaId = null;
				}
				break;
			}
			case 'SET_ACTIVE_MEDIA_ID': {
				draft.activeMediaId = action.payload.id;
				draft.medias.forEach(media => {
					if (media.options.autopause) {
						media.isPaused = media.id !== action.payload.id;
					}
				});
				break;
			}
			default:
				break;
		}
	});
}

const MediaContext = React.createContext<IMediaContext | null>(null);
const MediaDispatchContext = React.createContext<IMediaDispatchContext | null>(null);

const analytics = new Analytics({ processorsAmount: 5 });
const googleAnalytics = new GoogleAnalyticsObserver();
const storycardsAnalytics = new StorycardsAnalyticsObserver();

analytics.subscribe(googleAnalytics);
analytics.subscribe(storycardsAnalytics);

const MediaProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
	const [state, dispatch] = React.useReducer(mediaReducer, {
		analytics,
		activeMediaId: null,
		medias: [],
	});
	const { medias, activeMediaId } = state;
	const storyPageViewId = useClientSelector(storeState => storeState.user.storyPageViewId);
	const autoplayBatchRef = React.useRef<Set<string>>(new Set());
	const mediasRef = React.useRef(medias);
	mediasRef.current = medias;

	React.useEffect(() => {
		mediasRef.current.forEach(media => {
			if (!media?.player || !media.player.el()) {
				console.error('Video Context: Media player is not available');
				return;
			}

			if (
				(autoplayBatchRef.current.size && autoplayBatchRef.current.has(media.id)) ||
				media.id === activeMediaId
			) {
				media.player.play()?.catch(handlePlayError);
				media.player.muted(Boolean(media.options?.muted));
			} else {
				if (media.options.autopause) {
					media.player.pause();
				}
				if (media.player.paused() === false) {
					media.player.muted(true);
				}
			}
		});

		autoplayBatchRef.current.clear();
	}, [activeMediaId]);

	React.useEffect(() => {
		if (UrlHelperLocation.client.isPreview || UrlHelperLocation.client.isScreenshot()) {
			return () => {};
		}

		medias.forEach(media => {
			analytics.registerMedia(media);
		});

		return () =>
			medias.forEach(media => {
				analytics.unregisterMedia(media.id);
			});
	}, [medias, storyPageViewId]);

	const dispatchWithInterceptor = React.useCallback((action: Action) => {
		if (action.type === 'SET_ACTIVE_MEDIA_ID' && action.payload.autoplay) {
			autoplayBatchRef.current.add(action.payload.id);
		}

		dispatch(action);
	}, []);

	return (
		<MediaContext.Provider value={state}>
			<MediaDispatchContext.Provider value={{ dispatch: dispatchWithInterceptor }}>
				{children}
			</MediaDispatchContext.Provider>
		</MediaContext.Provider>
	);
};

const useMediaContext = () => {
	const context = React.useContext(MediaContext);

	return context;
};

const useMediaDispatchContext = () => {
	const context = React.useContext(MediaDispatchContext);

	return context?.dispatch;
};

const useMedia = () => {
	return [useMediaContext(), useMediaDispatchContext()] as const;
};

function handlePlayError(error: Error) {
	if (error.name === 'AbortError') {
		console.warn(error);
	} else {
		console.error(error);
	}
}

export { MediaProvider, useMediaContext, useMediaDispatchContext, useMedia };
